import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { SharedBarChart } from '../charts/SharedBarChart';
import { inferFormat, formatValue } from '../charts/chartUtils';
import { CopyImageButton } from './CopyImageButton';
import { LocalStorage } from '../../utils/LocalStorage';
import { notApplicableMetricsOf, tableFilterNotesOf } from '../../utils/filterNotes';

const MAX_SERIES = 4;
const TOP_N = 5;

/**
 * Agrupa las filas de la tabla por el valor de una dimensión dada,
 * sumando los valores de las métricas seleccionadas.
 * Retorna top N + "Otras".
 *
 * Una métrica que el nodo manda en `null` porque NO se puede calcular con los filtros (tanda
 * «Filtros que se respetan», review C-fe-04) no entra acá: `metricsThatApply` la saca antes, así
 * no sale como una barra en cero al lado de la tabla que dice «—». Los `null` sueltos de una
 * métrica que sí aplica siguen sumando 0, como siempre (sin notas, idéntico a hoy).
 */
export function buildGroupedData(tableData, dimensionName, metricNames, othersLabel) {
    if (!tableData || !tableData.rows || !dimensionName) return null;

    const dimKey = `dimension_${dimensionName}`;
    const groups = {};

    tableData.rows.forEach(row => {
        const dimCell = row[dimKey];
        const dimValue = (dimCell && (dimCell.formattedValue || dimCell.value)) || 'Sin dato';

        if (!groups[dimValue]) {
            groups[dimValue] = {};
            metricNames.forEach(m => { groups[dimValue][m] = 0; });
        }

        metricNames.forEach(m => {
            const metricKey = `metric_${m}`;
            const cell = row[metricKey];
            // Con comparación: currentValue; sin comparación: value
            const v = cell && (cell.currentValue ?? cell.value);
            if (v != null && !isNaN(v)) {
                groups[dimValue][m] = (groups[dimValue][m] || 0) + Number(v);
            }
        });
    });

    // Ordenar por primera métrica desc
    const firstMetric = metricNames[0];
    const sorted = Object.entries(groups).sort(([, a], [, b]) => (b[firstMetric] || 0) - (a[firstMetric] || 0));

    // Top N + Otras
    const top = sorted.slice(0, TOP_N);
    const rest = sorted.slice(TOP_N);

    if (rest.length > 0) {
        const othersGroup = {};
        metricNames.forEach(m => {
            othersGroup[m] = rest.reduce((acc, [, vals]) => acc + (vals[m] || 0), 0);
        });
        top.push([othersLabel, othersGroup]);
    }

    return top;
}

/** Las métricas elegidas que SÍ se pueden calcular con los filtros pedidos (`results.filter_notes`). */
export function metricsThatApply(metricNames, results) {
    const notApplicable = notApplicableMetricsOf(tableFilterNotesOf(results));
    return metricNames.filter((m) => !notApplicable.has(m));
}

class DataExplorerDimensionBarChartComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedDimension: null,
            selectedMetrics: [null, null],
            metricsDropdownOpen: false,
        };
        this.toggleMetricsDropdown = this.toggleMetricsDropdown.bind(this);
        this.handleDimensionChange = this.handleDimensionChange.bind(this);
        this.handleMetricToggle = this.handleMetricToggle.bind(this);
        this.closeDropdown = this.closeDropdown.bind(this);
    }

    componentDidMount() {
        this.initDefaults();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.lastFilters !== this.props.lastFilters) {
            this.syncSelections(prevProps.lastFilters);
        }
    }

    syncSelections(prevFilters) {
        const { lastFilters } = this.props;
        if (!lastFilters) return;

        const prevDims = (prevFilters && prevFilters.dimensions) || [];
        const prevMetrics = (prevFilters && prevFilters.metrics) || [];
        const newDims = lastFilters.dimensions || [];
        const newMetrics = lastFilters.metrics || [];

        const dimsChanged = prevDims.join(',') !== newDims.join(',');
        const metricsChanged = prevMetrics.join(',') !== newMetrics.join(',');

        if (!dimsChanged && !metricsChanged) return;

        const { selectedDimension, selectedMetrics } = this.state;

        const newSelectedDim = newDims.includes(selectedDimension)
            ? selectedDimension
            : (newDims.length > 0 ? newDims[newDims.length - 1] : null);

        const keptMetrics = selectedMetrics.filter(m => m !== null && newMetrics.includes(m));
        const needMore = keptMetrics.length === 0;
        const filledMetrics = needMore
            ? [newMetrics[0] || null, newMetrics[1] || null]
            : [...keptMetrics, ...Array(Math.max(0, 2 - keptMetrics.length)).fill(null)].slice(0, MAX_SERIES);

        this.setState({
            selectedDimension: newSelectedDim,
            selectedMetrics: filledMetrics,
        });
    }

    // `chartPrefs` explícitas (portal del cliente) ganan sobre las del navegador: ahí las
    // preferencias son parte de la vista que armó la agencia, no del historial de quien mira.
    resolveChartPrefs() {
        if (this.props.chartPrefs) return this.props.chartPrefs;
        return LocalStorage.getDataExplorerChartPreferences();
    }

    initDefaults() {
        const { lastFilters } = this.props;
        if (!lastFilters) return;

        const dims = lastFilters.dimensions || [];
        const metrics = lastFilters.metrics || [];

        const prefs = this.resolveChartPrefs();
        if (prefs) {
            const savedDim = prefs.barChartDimension && dims.includes(prefs.barChartDimension)
                ? prefs.barChartDimension : null;
            const savedMetrics = prefs.barChartMetrics
                ? prefs.barChartMetrics.map(m => m && metrics.includes(m) ? m : null)
                : null;
            const hasValidMetrics = savedMetrics && savedMetrics.some(m => m !== null);

            if (savedDim || hasValidMetrics) {
                this.setState({
                    selectedDimension: savedDim || (dims.length > 0 ? dims[dims.length - 1] : null),
                    selectedMetrics: hasValidMetrics ? savedMetrics : [metrics[0] || null, metrics[1] || null],
                });
                return;
            }
        }

        this.setState({
            selectedDimension: dims.length > 0 ? dims[dims.length - 1] : null,
            selectedMetrics: [metrics[0] || null, metrics[1] || null],
        });
    }

    getAvailableDimensions() {
        const { lastFilters } = this.props;
        return (lastFilters && lastFilters.dimensions) || [];
    }

    getAvailableMetrics() {
        const { lastFilters } = this.props;
        return (lastFilters && lastFilters.metrics) || [];
    }

    getFieldLabel(name) {
        const { fieldsData } = this.props;
        if (!fieldsData || !fieldsData.FIELD_DATA_BY_ID || !name) return name || '';
        const fd = fieldsData.FIELD_DATA_BY_ID[name];
        return fd ? fd.name : name;
    }

    handleDimensionChange(e) {
        const dim = e.target.value;
        this.setState({ selectedDimension: dim });
        this.saveBarChartPreference({ barChartDimension: dim });
    }

    handleMetricToggle(metricName) {
        this.setState(prev => {
            const current = prev.selectedMetrics.filter(m => m !== null);
            const isSelected = current.includes(metricName);
            let newMetrics;

            if (isSelected) {
                // Deseleccionar: al menos 1 métrica obligatoria
                if (current.length <= 1) return null;
                const next = prev.selectedMetrics.map(m => m === metricName ? null : m);
                // Compactar nulls al final
                const compacted = next.filter(m => m !== null);
                while (compacted.length < 2) compacted.push(null);
                newMetrics = compacted.slice(0, MAX_SERIES);
            } else {
                // Seleccionar: máx MAX_SERIES
                if (current.length >= MAX_SERIES) return null;
                const next = [...prev.selectedMetrics];
                const emptyIdx = next.findIndex(m => m === null);
                if (emptyIdx >= 0) {
                    next[emptyIdx] = metricName;
                } else {
                    next.push(metricName);
                }
                newMetrics = next.slice(0, MAX_SERIES);
            }

            this.saveBarChartPreference({ barChartMetrics: newMetrics });
            return { selectedMetrics: newMetrics };
        });
    }

    saveBarChartPreference(update) {
        if (!this.props.chartPrefs) {
            const prefs = LocalStorage.getDataExplorerChartPreferences() || {};
            LocalStorage.saveDataExplorerChartPreferences({ ...prefs, ...update });
        }
        if (this.props.onChartPrefsChange) this.props.onChartPrefsChange();
    }

    toggleMetricsDropdown() {
        this.setState(prev => ({ metricsDropdownOpen: !prev.metricsDropdownOpen }));
    }

    closeDropdown() {
        this.setState({ metricsDropdownOpen: false });
    }

    buildChartData() {
        const { results, t } = this.props;
        const { selectedDimension, selectedMetrics } = this.state;

        // Una métrica que no aplica con los filtros no entra al eje (ni como 0): la tabla de al lado
        // ya dice «—» y su motivo.
        const activeMetrics = metricsThatApply(selectedMetrics.filter(m => m !== null), results);
        if (!selectedDimension || activeMetrics.length === 0) return null;
        if (!results || !results.tableData) return null;

        const othersLabel = t('data_explorer.others', 'Otras');
        const grouped = buildGroupedData(results.tableData, selectedDimension, activeMetrics, othersLabel);
        if (!grouped || grouped.length === 0) return null;

        const labels = grouped.map(([dim]) => dim);

        const series = activeMetrics.map(metricName => {
            const label = this.getFieldLabel(metricName);
            const format = inferFormat(metricName);
            const data = grouped.map(([, vals]) => vals[metricName] || 0);
            const formattedData = data.map(v => formatValue(v, format));
            return {
                name: metricName,
                label,
                period: 'main',
                data,
                formattedData,
            };
        });

        return { labels, series };
    }

    render() {
        const { t } = this.props;
        const { selectedDimension, selectedMetrics, metricsDropdownOpen } = this.state;
        const dims = this.getAvailableDimensions();
        const allMetrics = this.getAvailableMetrics();
        const activeMetrics = selectedMetrics.filter(m => m !== null);

        if (dims.length === 0) {
            return (
                <div className="de-chart-block">
                    <div className="de-chart-block-header">
                        <h3>{t('data_explorer.dimension_bar_chart', 'Comparación por dimensión')}</h3>
                    </div>
                    <p className="de-chart-no-data">{t('data_explorer.select_dimension', 'Seleccioná al menos una dimensión')}</p>
                </div>
            );
        }

        const chartData = this.buildChartData();

        return (
            <CopyImageButton>
                {(copyBtn) => (
                <div className="de-chart-block">
                    <div className="de-chart-block-header">
                        <h3>{t('data_explorer.dimension_bar_chart', 'Comparación por dimensión')}</h3>
                        <div className="de-chart-controls">
                            {/* Selector de dimensión */}
                            <select
                                className="form-control form-control-sm"
                                value={selectedDimension || ''}
                                onChange={this.handleDimensionChange}
                            >
                                {dims.map(d => (
                                    <option key={d} value={d}>{this.getFieldLabel(d)}</option>
                                ))}
                            </select>

                            {/* Selector de métricas (checkboxes en dropdown) */}
                            <div className="de-metrics-dropdown">
                                <button
                                    className="de-metrics-dropdown__trigger"
                                    onClick={this.toggleMetricsDropdown}
                                >
                                    <span>{t('data_explorer.metrics_label', 'Métricas')}</span>
                                    <span className="de-metrics-dropdown__count">{activeMetrics.length}/{MAX_SERIES}</span>
                                </button>
                                {metricsDropdownOpen && (
                                    <div className="de-metrics-dropdown__menu" onMouseLeave={this.closeDropdown}>
                                        {allMetrics.map(m => {
                                            const isChecked = activeMetrics.includes(m);
                                            const isDisabled = !isChecked && activeMetrics.length >= MAX_SERIES;
                                            return (
                                                <label key={m} className={`de-metrics-dropdown__item${isChecked ? ' -checked' : ''}${isDisabled ? ' -disabled' : ''}`}>
                                                    <span className="de-metrics-dropdown__checkbox">
                                                        {isChecked && <span className="de-metrics-dropdown__check">✓</span>}
                                                    </span>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        disabled={isDisabled}
                                                        onChange={() => this.handleMetricToggle(m)}
                                                        className="de-metrics-dropdown__input"
                                                    />
                                                    <span className="de-metrics-dropdown__label">{this.getFieldLabel(m)}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            {copyBtn}
                        </div>
                    </div>

                    {chartData ? (
                        <div style={{ flex: 1, minHeight: 300 }}>
                            <SharedBarChart chartData={chartData} height="100%" />
                        </div>
                    ) : (
                        <p className="de-chart-no-data">{t('data_explorer.no_data', 'No hay datos para mostrar.')}</p>
                    )}
                </div>
                )}
            </CopyImageButton>
        );
    }
}

export const DataExplorerDimensionBarChart = withTranslation()(DataExplorerDimensionBarChartComponent);
